import type { AdminGame } from '@mcgames/contracts';
import { Button, Spinner, Tabs } from '@mcgames/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';
import { isNotFound } from '../../api/hooks.ts';
import { ArtworkTab } from './ArtworkTab.tsx';
import { DeleteGameControl } from './DeleteGameControl.tsx';
import { DetailsTab } from './DetailsTab.tsx';
import { ErrorText } from './ErrorText.tsx';
import { ExeReportsTab } from './ExeReportsTab.tsx';
import { GameStatusBadges } from './GameStatusBadges.tsx';
import { useAdminGame, useGameMutation } from './game-hooks.ts';
import styles from './Games.module.css';
import { ImportPanel } from './ImportPanel.tsx';
import type { InstallDraft } from './install-draft.ts';
import type { LinuxDraft } from './linux-draft.ts';
import { LinuxTab } from './LinuxTab.tsx';
import { VersionsTab } from './VersionsTab.tsx';

/** Tab keys double as the `?tab=` URL values; `labelKey` is the key below `games.tabs`. */
const TABS = [
  { key: 'details', labelKey: 'details' },
  { key: 'artwork', labelKey: 'artwork' },
  { key: 'versions', labelKey: 'versions' },
  { key: 'linux', labelKey: 'linux' },
  { key: 'exe-reports', labelKey: 'exeReports' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function isTabKey(value: string | null): value is TabKey {
  return TABS.some((tab) => tab.key === value);
}

/** The NZB and Install tabs became the Versions tab; their old links open it. */
const MOVED_TABS: Readonly<Record<string, TabKey>> = { nzb: 'versions', install: 'versions' };

function tabFromUrl(value: string | null): TabKey {
  if (isTabKey(value)) return value;
  return (value !== null ? MOVED_TABS[value] : undefined) ?? 'details';
}

export function GameEditorPage() {
  const { t } = useTranslation('admin');
  const { id = '' } = useParams();
  const game = useAdminGame(id);

  return (
    <div className={styles['page']}>
      <Link to="/admin/games" className={styles['back']}>
        {t('games.back')}
      </Link>
      {game.data !== undefined ? (
        <Editor key={game.data.id} game={game.data} />
      ) : game.isError ? (
        <p role="alert" className={styles['error']}>
          {t(isNotFound(game.error) ? 'games.notFound' : 'games.loadFailed')}
        </p>
      ) : (
        <Spinner />
      )}
    </div>
  );
}

function Editor({ game }: { game: AdminGame }) {
  const { t } = useTranslation('admin');
  const idPrefix = useId();
  const [params, setParams] = useSearchParams();
  const tab = tabFromUrl(params.get('tab'));
  // Unsaved edits of the install forms (per version) and the Linux form live here, so switching
  // tabs or versions does not drop them.
  const [installDrafts, setInstallDrafts] = useState<ReadonlyMap<string, InstallDraft>>(
    () => new Map(),
  );
  const [linuxDraft, setLinuxDraft] = useState<LinuxDraft | null>(null);

  function changeInstallDraft(versionId: string, draft: InstallDraft | null) {
    setInstallDrafts((drafts) => {
      const next = new Map(drafts);
      if (draft === null) next.delete(versionId);
      else next.set(versionId, draft);
      return next;
    });
  }

  function selectTab(key: string) {
    setParams(key === 'details' ? {} : { tab: key }, { replace: true });
  }

  return (
    <>
      <div className={styles['header']}>
        <div className={styles['headerText']}>
          <h1 className={styles['heading']}>{game.title}</h1>
          <GameStatusBadges game={game} />
        </div>
        <div className={styles['headerActions']}>
          <PublishControl game={game} />
          <DeleteGameControl game={game} />
        </div>
      </div>
      <ImportPanel gameId={game.id} gameTitle={game.title} />
      <div className={styles['tabsWrap']}>
        <Tabs
          idPrefix={idPrefix}
          items={TABS.map((item) => ({ key: item.key, label: t(`games.tabs.${item.labelKey}`) }))}
          value={tab}
          onChange={selectTab}
        />
      </div>
      <div
        role="tabpanel"
        id={`${idPrefix}-panel-${tab}`}
        aria-labelledby={`${idPrefix}-tab-${tab}`}
        tabIndex={0}
        className={styles['panel']}
      >
        {tab === 'details' && <DetailsTab game={game} />}
        {tab === 'artwork' && <ArtworkTab game={game} />}
        {tab === 'versions' && (
          <VersionsTab game={game} drafts={installDrafts} onDraftChange={changeInstallDraft} />
        )}
        {tab === 'linux' && (
          <LinuxTab game={game} draft={linuxDraft} onDraftChange={setLinuxDraft} />
        )}
        {tab === 'exe-reports' && <ExeReportsTab game={game} />}
      </div>
    </>
  );
}

/**
 * Publish is only possible with an available version: one whose NZB passed the check (`ok`, or
 * `repairable`: par2 restores the missing articles on the client) and that is not marked broken.
 * While there is none, the reason is shown as text tied to the button with `aria-describedby`: a
 * disabled button cannot be focused, so a hover tooltip alone would never reach keyboard users.
 */
function PublishControl({ game }: { game: AdminGame }) {
  const { t } = useTranslation('admin');
  const reasonId = useId();
  const publish = useGameMutation<void>(game.id, 'POST', '/publish');
  const unpublish = useGameMutation<void>(game.id, 'POST', '/unpublish');
  const blocked = !game.versions.some((version) => version.available);
  // With an `ok` or `repairable` best status, every such version is marked broken.
  const reason = game.nzbStatus ?? 'none';

  if (game.published) {
    return (
      <div className={styles['publish']}>
        <Button
          variant="secondary"
          disabled={unpublish.isPending}
          onClick={() => unpublish.mutate()}
        >
          {t('games.unpublish')}
        </Button>
        <ErrorText error={unpublish.error} />
      </div>
    );
  }
  return (
    <div className={styles['publish']}>
      <Button
        disabled={blocked || publish.isPending}
        aria-describedby={blocked ? reasonId : undefined}
        onClick={() => publish.mutate()}
      >
        {t('games.publish')}
      </Button>
      {blocked && (
        <span id={reasonId} className={styles['hint']}>
          {t(`games.publishBlocked.${reason}`)}
        </span>
      )}
      <ErrorText error={publish.error} />
    </div>
  );
}
